import type { ServerEvent } from "@/shared/types";
import { recordErrorLifecycle } from "./browser-errors";
import { listenForPushForeground } from "./push-foreground";

type ConnectionOptions = {
  path: () => string;
  onConnecting: () => void;
  onOpen?: () => void;
  onEvent: (event: ServerEvent) => void;
  onError: () => void;
  onWatchdog?: () => void;
};

/** Shared SSE lifecycle. Every reconnect resolves its cursor from current state. */
export function createSessionConnection(options: ConnectionOptions) {
  let source: EventSource | undefined;
  let watchdog: ReturnType<typeof setInterval> | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let lastActivity = 0;
  let lastWatchdog = 0;
  let lastTick = 0;
  let suspended = false;
  let active = false;
  let stopPushForeground: (() => void) | undefined;

  function stopSource() {
    clearTimeout(retry);
    clearInterval(watchdog);
    retry = undefined;
    watchdog = undefined;
    stopPushForeground?.();
    stopPushForeground = undefined;
    source?.close();
    source = undefined;
  }

  function connect() {
    stopSource();
    recordErrorLifecycle("reconnect");
    options.onConnecting();
    const current = new EventSource(options.path());
    source = current;
    stopPushForeground = listenForPushForeground(current);
    lastActivity = Date.now();
    lastWatchdog = lastActivity;
    lastTick = lastActivity;
    suspended = false;
    const touch = () => {
      if (source === current) lastActivity = Date.now();
    };
    current.addEventListener("heartbeat", touch);
    watchdog = setInterval(() => {
      const now = Date.now();
      suspended ||= now < lastTick || now - lastTick > 10_000;
      lastTick = now;
      if (document.visibilityState === "hidden") return;
      // Buffered heartbeats can arrive before this timer after sleep. They do
      // not prove the old socket still works; a paused event loop needs a fresh one.
      recover();
      if (Date.now() - lastWatchdog >= 15_000) {
        lastWatchdog = Date.now();
        options.onWatchdog?.();
      }
    }, 5000);
    current.onopen = () => {
      if (source !== current) return;
      clearTimeout(retry);
      retry = undefined;
      touch();
      options.onOpen?.();
    };
    current.onmessage = (message) => {
      if (source !== current) return;
      touch();
      options.onEvent(JSON.parse(message.data) as ServerEvent);
    };
    current.onerror = () => {
      if (source !== current) return;
      // Own retries instead of racing EventSource's automatic reconnect.
      stopSource();
      options.onError();
      retry = setTimeout(recover, 2000);
    };
  }

  function recover() {
    if (!active || !navigator.onLine) return;
    // A handshake that never opens must not inherit a live stream's lease.
    const timeout = source?.readyState === EventSource.CONNECTING ? 5000 : 15_000;
    const age = Date.now() - lastActivity;
    if (source && !suspended && age >= 0 && age < timeout) return;
    connect();
  }

  function offline() {
    stopSource();
    options.onError();
  }

  function visible() {
    if (document.visibilityState !== "hidden") recover();
  }

  function open() {
    if (!active) {
      active = true;
      window.addEventListener("online", recover);
      window.addEventListener("offline", offline);
      window.addEventListener("pageshow", recover);
      window.addEventListener("focus", recover);
      document.addEventListener("resume", visible);
      document.addEventListener("visibilitychange", visible);
    }
    if (navigator.onLine) recover();
    else offline();
  }

  function close() {
    active = false;
    stopSource();
    window.removeEventListener("online", recover);
    window.removeEventListener("offline", offline);
    window.removeEventListener("pageshow", recover);
    window.removeEventListener("focus", recover);
    document.removeEventListener("resume", visible);
    document.removeEventListener("visibilitychange", visible);
  }

  return { open, recover, close };
}
